import { useState, useEffect, useCallback, useRef } from "react";
import ToolIcon from "./ToolIcon";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";
const API_UNCONFIGURED = API_BASE.includes("your-api-gateway");

const TASK_TYPE_OPTIONS = [
  { value: "ENH", label: "ENH - Enhancements" },
  { value: "DEF", label: "DEF - Defects" },
  { value: "PRJ", label: "PRJ - Projects" },
  { value: "PI", label: "PI - Process Improvements" },
  { value: "RNM", label: "RNM - Run and Maintain" },
];

const APP_OPTIONS = [
  { value: "AVA", label: "AVA - Avature" },
  { value: "GHR", label: "GHR - myHR" },
  { value: "SAP", label: "SAP - SAP" },
  { value: "IDN", label: "IDN - Identity Now" },
  { value: "UKG", label: "UKG - Time and Attendance" },
];

function toTimestamp(dateValue) {
  if (!dateValue) return Number.MAX_SAFE_INTEGER;
  const parsed = Date.parse(dateValue);
  if (!Number.isNaN(parsed)) return parsed;

  // Handle values like "07-01-2026 11:00:00 PM"
  const m = String(dateValue).match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM))?$/i);
  if (!m) return Number.MAX_SAFE_INTEGER;

  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  let hour = Number(m[4] || 0);
  const minute = Number(m[5] || 0);
  const second = Number(m[6] || 0);
  const ampm = (m[7] || "").toUpperCase();

  if (ampm === "PM" && hour < 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;

  return new Date(year, month - 1, day, hour, minute, second).getTime();
}

function displayText(value, fallback = "—") {
  if (value == null || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    return value.display_value || value.value || value.name || fallback;
  }
  return fallback;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

async function apiRequest(path, options = {}) {
  try {
    const method = (options.method || "GET").toUpperCase();
    const hasBody = options.body != null;
    const defaultHeaders = hasBody || method !== "GET"
      ? { "Content-Type": "application/json" }
      : {};

    const res = await fetch(`${API_BASE}${path}`, {
      headers: { ...defaultHeaders, ...(options.headers || {}) },
      ...options,
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.error || `API error ${res.status}`);
    }
    return data;
  } catch (err) {
    if (err instanceof TypeError) {
      throw new Error("Cannot reach AWS API. Configure API Gateway URL/CORS and verify Lambda deployment.");
    }
    throw err;
  }
}

// ── Sprint view ────────────────────────────────────────────────────────────
function SprintView({ sprint, stories, onCreateStory, onCreateMultipleStories, loading, assigneeFilter }) {
  const [expanded, setExpanded] = useState(false);

  const filteredStories = assigneeFilter
    ? (stories || []).filter(s => displayText(s.assigned_to, "").toLowerCase().includes(assigneeFilter.toLowerCase()))
    : (stories || []);
  
  return (
    <div className="sn-sprint-card">
      <div 
        className="sn-sprint-header"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="sn-sprint-title">
          <span className="sn-sprint-toggle">{expanded ? "▼" : "▶"}</span>
          <span className="sn-sprint-name">{sprint.name}</span>
          <span className="sn-sprint-state">{sprint.state}</span>
        </div>
        <div className="sn-sprint-meta">
          <span className="sn-story-count">{assigneeFilter ? `${filteredStories.length} / ${stories?.length || 0}` : (stories?.length || 0)} stories</span>
          <span className="sn-sprint-date">
            {sprint.start_date && new Date(sprint.start_date).toLocaleDateString()}
            {sprint.end_date && ` - ${new Date(sprint.end_date).toLocaleDateString()}`}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="sn-sprint-body">
          {stories && stories.length > 0 ? (
            <div className="sn-stories-list">
              <div className="sn-stories-header">
                <div className="sn-story-col sn-story-col-key">Story Number</div>
                <div className="sn-story-col sn-story-col-summary">Summary</div>
                <div className="sn-story-col sn-story-col-points">Points</div>
                <div className="sn-story-col sn-story-col-status">Status</div>
                <div className="sn-story-col sn-story-col-assignee">Assignee</div>
              </div>
              {filteredStories.length === 0 ? (
                <div className="sn-empty-state">No stories match the selected assignee</div>
              ) : filteredStories.map(story => (
                <div key={story.sys_id} className={`sn-story-row sn-story-${story.state?.toLowerCase() || "unknown"}`}>
                  <div className="sn-story-col sn-story-col-key">{displayText(story.number)}</div>
                  <div className="sn-story-col sn-story-col-summary" title={displayText(story.title)}>{displayText(story.title)}</div>
                  <div className="sn-story-col sn-story-col-points">{displayText(story.story_points, "—")}</div>
                  <div className="sn-story-col sn-story-col-status">{displayText(story.state)}</div>
                  <div className="sn-story-col sn-story-col-assignee">{displayText(story.assigned_to, "Unassigned")}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="sn-empty-state">No stories in this sprint</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Create story modal ──────────────────────────────────────────────────────
function CreateStoryModal({ sprintId, sprints, onClose, onSubmit, loading }) {
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [targetSprintIds, setTargetSprintIds] = useState([sprintId]);
  const [error, setError] = useState("");

  useEffect(() => {
    setTargetSprintIds([sprintId]);
  }, [sprintId]);

  const toggleSprint = (id) => {
    setTargetSprintIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!summary.trim()) {
      setError("Summary is required");
      return;
    }
    if (targetSprintIds.length === 0) {
      setError("Select at least one target sprint");
      return;
    }
    try {
      await onSubmit({ sprintId, sprintIds: targetSprintIds, summary, description });
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Story</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {error && <div className="sn-error-bar">{error}</div>}

          <div className="form-group">
            <label className="control-label">Target Sprints *</label>
            <div className="sn-sprint-targets">
              {(sprints || []).map((s) => (
                <label key={s.sys_id} className="sn-sprint-target-row">
                  <input
                    type="checkbox"
                    checked={targetSprintIds.includes(s.sys_id)}
                    onChange={() => toggleSprint(s.sys_id)}
                    disabled={loading}
                  />
                  <span>{s.name}</span>
                </label>
              ))}
            </div>
            <div className="form-hint">{targetSprintIds.length} sprint(s) selected</div>
          </div>
          
          <div className="form-group">
            <label className="control-label">Summary *</label>
            <input 
              className="form-control"
              placeholder="Enter story summary…"
              value={summary}
              onChange={e => setSummary(e.target.value)}
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="control-label">Description</label>
            <textarea 
              className="form-control form-textarea"
              placeholder="Enter story description…"
              rows="6"
              value={description}
              onChange={e => setDescription(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSubmit}
            disabled={loading || !summary.trim() || targetSprintIds.length === 0}
          >
            {loading ? "Creating…" : "Create Story"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create multiple stories modal ───────────────────────────────────────────
function CreateMultipleStoriesModal({ sprintId, sprints, onClose, onSubmit, loading }) {
  const [storiesText, setStoriesText] = useState("");
  const [targetSprintIds, setTargetSprintIds] = useState([sprintId]);
  const [error, setError] = useState("");

  useEffect(() => {
    setTargetSprintIds([sprintId]);
  }, [sprintId]);

  const toggleSprint = (id) => {
    setTargetSprintIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    const lines = storiesText
      .split("\n")
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (lines.length === 0) {
      setError("Enter at least one story summary");
      return;
    }
    if (targetSprintIds.length === 0) {
      setError("Select at least one target sprint");
      return;
    }

    try {
      await onSubmit({ 
        sprintId, 
        sprintIds: targetSprintIds,
        stories: lines.map(summary => ({ summary }))
      });
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card modal-card-wide" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Multiple Stories</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {error && <div className="sn-error-bar">{error}</div>}

          <div className="form-group">
            <label className="control-label">Target Sprints *</label>
            <div className="sn-sprint-targets">
              {(sprints || []).map((s) => (
                <label key={s.sys_id} className="sn-sprint-target-row">
                  <input
                    type="checkbox"
                    checked={targetSprintIds.includes(s.sys_id)}
                    onChange={() => toggleSprint(s.sys_id)}
                    disabled={loading}
                  />
                  <span>{s.name}</span>
                </label>
              ))}
            </div>
            <div className="form-hint">{targetSprintIds.length} sprint(s) selected</div>
          </div>
          
          <div className="form-group">
            <label className="control-label">Story Summaries (one per line) *</label>
            <textarea 
              className="form-control form-textarea"
              placeholder="Enter one story summary per line…"
              rows="12"
              value={storiesText}
              onChange={e => setStoriesText(e.target.value)}
              disabled={loading}
              autoFocus
            />
            <div className="form-hint">
              {storiesText.split("\n").filter(l => l.trim().length > 0).length} stories will be created
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSubmit}
            disabled={loading || storiesText.split("\n").filter(l => l.trim().length > 0).length === 0 || targetSprintIds.length === 0}
          >
            {loading ? "Creating…" : `Create ${storiesText.split("\n").filter(l => l.trim().length > 0).length} Stories`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function ServiceNowActions({ theme = "light" }) {
  const [sprints, setSprints] = useState(null);
  const [sprintStories, setSprintStories] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [loadingSprintId, setLoadingSprintId] = useState(null);

  const [activeTab, setActiveTab] = useState("view");

  // Epics
  const [epics, setEpics] = useState(null);

  // Create single story tab
  const [singleSprintId, setSingleSprintId] = useState("");
  const [singleEpicId, setSingleEpicId] = useState("");
  const [singleStoryPoints, setSingleStoryPoints] = useState("");
  const [singleSummary, setSingleSummary] = useState("");
  const [singleDescription, setSingleDescription] = useState("");
  const [singleTaskType, setSingleTaskType] = useState("");
  const [singleAppCode, setSingleAppCode] = useState("");
  const [singleIncludeSprint, setSingleIncludeSprint] = useState(false);
  const [singleIncludeMonth, setSingleIncludeMonth] = useState(false);

  // Create multiple stories tab
  const [multiSprintIds, setMultiSprintIds] = useState([]);
  const [multiEpicId, setMultiEpicId] = useState("");
  const [multiStoryPoints, setMultiStoryPoints] = useState("");
  const [multiDescription, setMultiDescription] = useState("");
  const [multiStoriesText, setMultiStoriesText] = useState("");
  const [multiTaskType, setMultiTaskType] = useState("");
  const [multiAppCode, setMultiAppCode] = useState("");
  const [multiIncludeSprint, setMultiIncludeSprint] = useState(false);
  const [multiIncludeMonth, setMultiIncludeMonth] = useState(false);

  // View tab filter
  const [viewAssigneeFilter, setViewAssigneeFilter] = useState("");

  // Delete stories tab
  const [deleteSprintId, setDeleteSprintId] = useState("");
  const [deletingStoryId, setDeletingStoryId] = useState("");
  const [selectedStoriesForDelete, setSelectedStoriesForDelete] = useState([]);
  const [confirmDeleteSelected, setConfirmDeleteSelected] = useState(false);
  const [confirmDeleteCheckbox, setConfirmDeleteCheckbox] = useState(false);

  // Unplanned Work tab
  const [unplannedAssignmentGroup, setUnplannedAssignmentGroup] = useState("");
  const [unplannedTaskTypes, setUnplannedTaskTypes] = useState(["incident", "sc_task", "sn_hr_core_case", "change_request"]);
  const [unplannedStates, setUnplannedStates] = useState([]);
  const [unplannedOpenedAfter, setUnplannedOpenedAfter] = useState("");
  const [unplannedClosedAfter, setUnplannedClosedAfter] = useState("");
  const [unplannedAssignedTo, setUnplannedAssignedTo] = useState([]);
  const [unplannedAssigneeDropdownOpen, setUnplannedAssigneeDropdownOpen] = useState(false);
  const [unplannedTaskTypeDropdownOpen, setUnplannedTaskTypeDropdownOpen] = useState(false);
  const [unplannedStateDropdownOpen, setUnplannedStateDropdownOpen] = useState(false);
  const [unplannedResults, setUnplannedResults] = useState(null);
  const [unplannedLoading, setUnplannedLoading] = useState(false);
  const assigneeDropdownRef = useRef(null);
  const taskTypeDropdownRef = useRef(null);
  const stateDropdownRef = useRef(null);

  // Team Metrics tab
  const [metricsData, setMetricsData] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsPeriod, setMetricsPeriod] = useState("this_month");

  // Assignment group to members mapping
  const ASSIGNMENT_GROUP_MEMBERS = {
    "347921630fd68a006ee822d8b1050ebc": [ // KGS HRT Application Security
      "0e6625b41b34fbc0515aecadee4bcb85", // Abhishek Sinha
      "ae6f69f0c31802947511febf050131d2", // Adarsh Naikode
      "d1822a67932d1e109a2c78fdfaba1013", // Cath Barba
      "c174f49337e14200d4e8daa543990ed3", // Dennis Finch
      "70d830b41b0ab3c0591e85507e4bcbe9", // Dhairya Shah
      "f11f5d0d3782c600df4d6bc2b3990e2a", // John Marshall
      "483c59493782c600df4d6bc2b3990e49", // Kim Young
      "d76374ba1b7138509fb0cb7b234bcb30", // LaTanya Morrison
      "f8361be0db2ca51c16db8b4d13961985", // Manish Mukherjee
      "103089ff1b70081047a264207e4bcba4", // Muralidhar Shenoy
      "b76308391bb1401047a264207e4bcb87", // Nabil Fatwa
      "882c3d14db796c94730b9d67b99619ab", // Swathi Chandru
      "ae292654c335a95c54a59dec7a013165", // Tyler Pennick
      "f99c282733581218cbffce167d5c7b13", // VARSHA JALIYAL
      "8ea094c3dbe1501036cbc8cb13961971", // Vinita Yadav
      "666bec2cdb4e485085ec8c6a6896195c", // Yamini Rajagopal
    ],
    "b1b9e1630fd68a006ee822d8b1050e7e": [ // KBS HR SAP Basis System Admin
      "f11f5d0d3782c600df4d6bc2b3990e2a", // John Marshall
      "af7afe7bdb6a64104d53f135b9961907", // Rajkamal Kalita
      "72542503db7dd1102ed59345ca9619fb", // Sandhya Singh
    ],
    "b9b9e1630fd68a006ee822d8b1050e7b": [ // KGS HRT Cloud Infrastructure
      "5b9579fb6fe00a0026a6bce0be3ee4df", // Andrew Trapp
      "01c9b2f56f63d20058e99df44b3ee447", // Charlotte Aldridge
      "06f5653593d21610296af8566aba1083", // Christopher Ladron de Guevara
      "70d830b41b0ab3c0591e85507e4bcbe9", // Dhairya Shah
      "45e6c270c3b58614afce92977d01310e", // Hemadri Thanneeru
      "f11f5d0d3782c600df4d6bc2b3990e2a", // John Marshall
      "483c59493782c600df4d6bc2b3990e49", // Kim Young
      "a20d9bb2c3903d10c7fed4ec7a01315f", // Marcelo Almaraz Hernandez
      "103089ff1b70081047a264207e4bcba4", // Muralidhar Shenoy
      "b76308391bb1401047a264207e4bcb87", // Nabil Fatwa
      "af7afe7bdb6a64104d53f135b9961907", // Rajkamal Kalita
      "219c04e6c3ecca949149b4ee05013141", // Richy Rodriguez
      "72542503db7dd1102ed59345ca9619fb", // Sandhya Singh
      "ae292654c335a95c54a59dec7a013165", // Tyler Pennick
    ],
    "4eb9e1630fd68a006ee822d8b1050e8b": [ // KBS HR SAP Transports - Basis Team
      "f11f5d0d3782c600df4d6bc2b3990e2a", // John Marshall
      "af7afe7bdb6a64104d53f135b9961907", // Rajkamal Kalita
      "72542503db7dd1102ed59345ca9619fb", // Sandhya Singh
    ],
  };

  // All unique assignees across all groups
  const HRTSI_ASSIGNEES = [
    { value: "0e6625b41b34fbc0515aecadee4bcb85", name: "Abhishek Sinha" },
    { value: "ae6f69f0c31802947511febf050131d2", name: "Adarsh Naikode" },
    { value: "5b9579fb6fe00a0026a6bce0be3ee4df", name: "Andrew Trapp" },
    { value: "d1822a67932d1e109a2c78fdfaba1013", name: "Cath Barba" },
    { value: "01c9b2f56f63d20058e99df44b3ee447", name: "Charlotte Aldridge" },
    { value: "06f5653593d21610296af8566aba1083", name: "Christopher Ladron de Guevara" },
    { value: "c174f49337e14200d4e8daa543990ed3", name: "Dennis Finch" },
    { value: "70d830b41b0ab3c0591e85507e4bcbe9", name: "Dhairya Shah" },
    { value: "45e6c270c3b58614afce92977d01310e", name: "Hemadri Thanneeru" },
    { value: "f11f5d0d3782c600df4d6bc2b3990e2a", name: "John Marshall" },
    { value: "483c59493782c600df4d6bc2b3990e49", name: "Kim Young" },
    { value: "d76374ba1b7138509fb0cb7b234bcb30", name: "LaTanya Morrison" },
    { value: "f8361be0db2ca51c16db8b4d13961985", name: "Manish Mukherjee" },
    { value: "a20d9bb2c3903d10c7fed4ec7a01315f", name: "Marcelo Almaraz Hernandez" },
    { value: "103089ff1b70081047a264207e4bcba4", name: "Muralidhar Shenoy" },
    { value: "b76308391bb1401047a264207e4bcb87", name: "Nabil Fatwa" },
    { value: "af7afe7bdb6a64104d53f135b9961907", name: "Rajkamal Kalita" },
    { value: "219c04e6c3ecca949149b4ee05013141", name: "Richy Rodriguez" },
    { value: "72542503db7dd1102ed59345ca9619fb", name: "Sandhya Singh" },
    { value: "882c3d14db796c94730b9d67b99619ab", name: "Swathi Chandru" },
    { value: "ae292654c335a95c54a59dec7a013165", name: "Tyler Pennick" },
    { value: "f99c282733581218cbffce167d5c7b13", name: "VARSHA JALIYAL" },
    { value: "8ea094c3dbe1501036cbc8cb13961971", name: "Vinita Yadav" },
    { value: "666bec2cdb4e485085ec8c6a6896195c", name: "Yamini Rajagopal" },
  ];

  // Filter assignees based on selected assignment group
  const filteredAssignees = unplannedAssignmentGroup
    ? HRTSI_ASSIGNEES.filter(a => ASSIGNMENT_GROUP_MEMBERS[unplannedAssignmentGroup]?.includes(a.value))
    : HRTSI_ASSIGNEES;

  const [creatingStory, setCreatingStory] = useState(false);

  // Load sprints and epics on mount
  useEffect(() => {
    loadSprints();
    loadEpics();
  }, []);

  const loadSprints = useCallback(async () => {
    if (API_UNCONFIGURED) {
      setError("REACT_APP_API_BASE is not configured. Set it to your API Gateway stage URL.");
      setSprints([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest("/sn-sprints");
      const sortedSprints = [...(data.sprints || [])].sort(
        (a, b) => toTimestamp(a.start_date) - toTimestamp(b.start_date)
      );
      setSprints(sortedSprints);
      // Load stories for each sprint
      if (sortedSprints.length > 0) {
        for (const sprint of sortedSprints) {
          await loadSprintStories(sprint.sys_id);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEpics = useCallback(async () => {
    if (API_UNCONFIGURED) return;
    try {
      const data = await apiRequest("/sn-epics");
      setEpics(data.epics || []);
    } catch {
      // Retry once for transient API Gateway throttles/CORS hiccups.
      try {
        const retry = await apiRequest("/sn-epics");
        setEpics(retry.epics || []);
      } catch {
        // Preserve existing loaded epics if present.
        setEpics((prev) => (Array.isArray(prev) && prev.length > 0 ? prev : []));
      }
    }
  }, []);

  const loadSprintStories = useCallback(async (sprintId) => {
    setLoadingSprintId(sprintId);
    try {
      const data = await apiRequest(`/sn-stories?sprintId=${encodeURIComponent(sprintId)}`);
      setSprintStories(prev => ({ ...prev, [sprintId]: data.stories || [] }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSprintId(null);
    }
  }, []);

  const buildSprintToken = useCallback((sprintId) => {
    const sprintName = (sprints || []).find((s) => s.sys_id === sprintId)?.name || "";
    const sprintMatch = sprintName.match(/sprint\s*[-_ ]*(\d+)/i);
    return sprintMatch
      ? `Sprint${sprintMatch[1]}`
      : (sprintName || "Sprint").replace(/\s+/g, "");
  }, [sprints]);

  const buildSprintMonthToken = useCallback((sprintId) => {
    const sprint = (sprints || []).find((s) => s.sys_id === sprintId);
    if (!sprint) return "";

    const start = sprint.start_date ? new Date(sprint.start_date) : null;
    const end = sprint.end_date ? new Date(sprint.end_date) : null;
    const startValid = start && !Number.isNaN(start.getTime());
    const endValid = end && !Number.isNaN(end.getTime());

    if (startValid && endValid) {
      const startMonth = start.toLocaleString("en-US", { month: "short" });
      const endMonth = end.toLocaleString("en-US", { month: "short" });
      const startYear = start.getFullYear();
      const endYear = end.getFullYear();

      if (startMonth === endMonth && startYear === endYear) {
        return `${startMonth}${startYear}`;
      }

      return `${startMonth}${startYear}to${endMonth}${endYear}`;
    }

    if (startValid) {
      return `${start.toLocaleString("en-US", { month: "short" })}${start.getFullYear()}`;
    }

    if (endValid) {
      return `${end.toLocaleString("en-US", { month: "short" })}${end.getFullYear()}`;
    }

    return "";
  }, [sprints]);

  const buildConventionShortDescription = useCallback((sprintId, taskType, appCode, includeSprint, includeMonth, baseShortDescription) => {
    const trimmed = String(baseShortDescription || "").replace(/\r?\n+/g, " ").trim();
    const withoutOldPrefix = trimmed
      .replace(/^HRTSI\s*-\s*[A-Z]+\s*-\s*[A-Z]+(?:\s*-\s*[A-Za-z]{3}\d{4}(?:to[A-Za-z]{3}\d{4})?)?(?:\s*-\s*Sprint\d+)?\s*[-:]?\s*/i, "")
      .trimStart();

    const parts = ["HRTSI", taskType, appCode];
    if (includeSprint) {
      parts.push(buildSprintToken(sprintId));
    }
    if (includeMonth) {
      const monthToken = buildSprintMonthToken(sprintId);
      if (monthToken) parts.push(monthToken);
    }
    if (withoutOldPrefix) {
      parts.push(withoutOldPrefix);
    }

    return parts.join("-");
  }, [buildSprintMonthToken, buildSprintToken]);

  const handleCreateStory = useCallback(async (payload) => {
    setCreatingStory(true);
    try {
      const sprintIds = payload.sprintIds || [payload.sprintId];
      const combined = { created: [], failed: [] };

      for (const sprintId of sprintIds) {
        const shortDescription = buildConventionShortDescription(sprintId, payload.taskType, payload.appCode, payload.includeSprint, payload.includeMonth, payload.summary);
        const data = await apiRequest("/sn-stories", {
          method: "POST",
          body: JSON.stringify({
            sprint: sprintId,
            sprintIds: [sprintId],
            stories: [
              {
                short_description: shortDescription,
                description: String(payload.description || "").trim(),
                classification: "Story",
                ...(payload.epicId ? { epic: payload.epicId } : {}),
                ...(payload.storyPoints !== "" ? { story_points: String(payload.storyPoints) } : {}),
              },
            ],
          }),
        });
        combined.created.push(...asArray(data?.created));
        combined.failed.push(...asArray(data?.failed));
      }

      for (const sprintId of sprintIds) {
        await loadSprintStories(sprintId);
      }
      return combined;
    } catch (err) {
      throw err;
    } finally {
      setCreatingStory(false);
    }
  }, [buildConventionShortDescription, loadSprintStories]);

  const handleCreateMultipleStories = useCallback(async (payload) => {
    setCreatingStory(true);
    try {
      const sprintIds = payload.sprintIds || [payload.sprintId];
      const combined = { created: [], failed: [] };

      for (const sprintId of sprintIds) {
        const trimmedDescription = String(payload.description || "").trim();
        const data = await apiRequest("/sn-stories", {
          method: "POST",
          body: JSON.stringify({
            sprint: sprintId,
            sprintIds: [sprintId],
            stories: payload.stories.map((story) => ({
              short_description: buildConventionShortDescription(sprintId, payload.taskType, payload.appCode, payload.includeSprint, payload.includeMonth, story.summary),
              description: trimmedDescription,
              classification: "Story",
              ...(payload.epicId ? { epic: payload.epicId } : {}),
              ...(payload.storyPoints !== "" ? { story_points: String(payload.storyPoints) } : {}),
            })),
          }),
        });
        combined.created.push(...asArray(data?.created));
        combined.failed.push(...asArray(data?.failed));
      }

      for (const sprintId of sprintIds) {
        await loadSprintStories(sprintId);
      }
      return combined;
    } catch (err) {
      throw err;
    } finally {
      setCreatingStory(false);
    }
  }, [buildConventionShortDescription, loadSprintStories]);

  useEffect(() => {
    if (!sprints || sprints.length === 0) return;

    const firstSprintId = sprints[0].sys_id;
    // singleSprintId intentionally left empty (user selects sprint)
    if (!deleteSprintId) setDeleteSprintId(firstSprintId);
  }, [sprints, singleSprintId, multiSprintIds, deleteSprintId]);

  useEffect(() => {
    if (!deleteSprintId) return;
    if (sprintStories[deleteSprintId]) return;
    loadSprintStories(deleteSprintId);
  }, [deleteSprintId, sprintStories, loadSprintStories]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        setUnplannedAssigneeDropdownOpen(false);
      }
      if (taskTypeDropdownRef.current && !taskTypeDropdownRef.current.contains(event.target)) {
        setUnplannedTaskTypeDropdownOpen(false);
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setUnplannedStateDropdownOpen(false);
      }
    };

    if (unplannedAssigneeDropdownOpen || unplannedTaskTypeDropdownOpen || unplannedStateDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [unplannedAssigneeDropdownOpen, unplannedTaskTypeDropdownOpen, unplannedStateDropdownOpen]);

  const toggleMultiSprint = (id) => {
    setMultiSprintIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const submitCreateSingle = async () => {
    if (!singleSummary.trim()) {
      setError("Summary is required");
      return;
    }
    if (!singleTaskType) {
      setError("Task type is required");
      return;
    }
    if (!singleAppCode) {
      setError("Application is required");
      return;
    }
    if (!singleSprintId) {
      setError("Select a target sprint");
      return;
    }
    setError(null);
    setResult(null);
    try {
      const data = await handleCreateStory({
        sprintId: singleSprintId,
        sprintIds: [singleSprintId],
        summary: singleSummary,
        description: singleDescription,
        epicId: singleEpicId,
        storyPoints: singleStoryPoints,
        taskType: singleTaskType,
        appCode: singleAppCode,
        includeSprint: singleIncludeSprint,
        includeMonth: singleIncludeMonth,
      });
      const createdItems = asArray(data?.created);
      const failedItems = asArray(data?.failed);

      if (createdItems.length === 0) {
        const firstError = failedItems[0]?.error || "No story was created.";
        setResult({ success: false, message: firstError });
        return;
      }

      const firstCreated = createdItems[0] || {};
      const storyNumber = firstCreated.number || firstCreated.sys_id || "(number unavailable)";
      setResult({
        success: true,
        message: `Story ${storyNumber} created successfully.`,
      });
      setSingleSummary("");
      setSingleDescription("");
      setSingleEpicId("");
      setSingleStoryPoints("");
      setSingleTaskType("");
      setSingleAppCode("");
      setSingleIncludeSprint(false);
      setSingleIncludeMonth(false);
    } catch (err) {
      setResult({ success: false, message: err.message || "Failed to create story." });
    }
  };

  const submitCreateMultiple = async () => {
    const shortDescription = multiStoriesText.replace(/\r?\n+/g, " ").trim();
    const trimmedPoints = String(multiStoryPoints || "").trim();

    if (!shortDescription) {
      setError("Enter at least one short description");
      return;
    }
    if (!multiTaskType) {
      setError("Task type is required");
      return;
    }
    if (!multiAppCode) {
      setError("Application is required");
      return;
    }
    if (!multiEpicId) {
      setError("Epic is required");
      return;
    }
    if (multiSprintIds.length === 0) {
      setError("Select at least one target sprint");
      return;
    }
    if (!trimmedPoints) {
      setError("Story points is required");
      return;
    }

    setError(null);
    setResult(null);
    try {
      const data = await handleCreateMultipleStories({
        sprintId: multiSprintIds[0],
        sprintIds: multiSprintIds,
        epicId: multiEpicId,
        storyPoints: trimmedPoints,
        description: multiDescription,
        taskType: multiTaskType,
        appCode: multiAppCode,
        includeSprint: multiIncludeSprint,
        includeMonth: multiIncludeMonth,
        stories: [{ summary: shortDescription }],
      });

      const createdItems = asArray(data?.created);
      const failedItems = asArray(data?.failed);
      const createdCount = createdItems.length;
      const failedCount = failedItems.length;
      const createdNumbers = createdItems
        .map((item) => item?.number)
        .filter(Boolean)
        .slice(0, 5);
      const numbersText = createdNumbers.length > 0 ? ` Story numbers: ${createdNumbers.join(", ")}${createdCount > 5 ? ", ..." : ""}.` : "";

      if (createdCount === 0) {
        const firstError = failedItems[0]?.error || "No stories were created.";
        setResult({ success: false, message: firstError });
        return;
      }

      if (failedCount > 0) {
        setResult({
          success: false,
          message: `${createdCount} created, ${failedCount} failed.${numbersText}`,
        });
      } else {
        setResult({
          success: true,
          message: `${createdCount} stories created successfully.${numbersText}`,
        });
      }
      setMultiStoriesText("");
      setMultiDescription("");
      setMultiEpicId("");
      setMultiStoryPoints("");
      setMultiTaskType("");
      setMultiAppCode("");
      setMultiIncludeSprint(false);
      setMultiIncludeMonth(false);
    } catch (err) {
      setResult({ success: false, message: err.message || "Failed to create stories." });
    }
  };

  const toggleStorySelection = (storySysId) => {
    setSelectedStoriesForDelete(prev =>
      prev.includes(storySysId)
        ? prev.filter(id => id !== storySysId)
        : [...prev, storySysId]
    );
  };

  const handleDeleteStory = async (storySysId, sprintId, storyNumber) => {
    if (!storySysId) return;
    setDeletingStoryId(storySysId);
    setError(null);
    setResult(null);
    try {
      await apiRequest("/sn-stories", {
        method: "POST",
        body: JSON.stringify({
          operation: "delete",
          storySysId,
        }),
      });
      await loadSprintStories(sprintId);
      const deletedLabel = storyNumber || storySysId;
      setResult({ success: true, message: `Story ${deletedLabel} deleted successfully.` });
    } catch (err) {
      setResult({ success: false, message: err.message || "Failed to delete story." });
    } finally {
      setDeletingStoryId("");
    }
  };

  const handleDeleteBatch = async () => {
    if (selectedStoriesForDelete.length === 0 || !confirmDeleteCheckbox) return;
    
    setDeletingStoryId("batch");
    setError(null);
    setResult(null);
    
    try {
      const deletedCount = { success: 0, failed: 0 };
      const failedStories = [];

      for (const storySysId of selectedStoriesForDelete) {
        try {
          await apiRequest("/sn-stories", {
            method: "POST",
            body: JSON.stringify({
              operation: "delete",
              storySysId,
            }),
          });
          deletedCount.success++;
        } catch (err) {
          deletedCount.failed++;
          failedStories.push(storySysId);
        }
      }

      await loadSprintStories(deleteSprintId);
      setSelectedStoriesForDelete([]);
      setConfirmDeleteSelected(false);
      setConfirmDeleteCheckbox(false);

      if (deletedCount.failed === 0) {
        setResult({
          success: true,
          message: `${deletedCount.success} stories deleted successfully.`,
        });
      } else {
        setResult({
          success: false,
          message: `${deletedCount.success} deleted, ${deletedCount.failed} failed.`,
        });
      }
    } catch (err) {
      setResult({ success: false, message: err.message || "Failed to delete stories." });
    } finally {
      setDeletingStoryId("");
    }
  };

  const searchUnplannedWork = async () => {
    if (!unplannedAssignmentGroup) {
      setError("Please select an assignment group");
      return;
    }

    setUnplannedLoading(true);
    setError(null);
    setUnplannedResults(null);

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("assignmentGroup", unplannedAssignmentGroup);
      
      if (unplannedTaskTypes.length > 0) {
        queryParams.append("taskTypes", unplannedTaskTypes.join(","));
      }
      
      if (unplannedStates.length > 0) {
        queryParams.append("states", unplannedStates.join(","));
      }
      
      if (unplannedOpenedAfter) {
        queryParams.append("openedAfter", unplannedOpenedAfter);
      }
      
      if (unplannedClosedAfter) {
        queryParams.append("closedAfter", unplannedClosedAfter);
      }
      
      if (unplannedAssignedTo.length > 0) {
        queryParams.append("assignedTo", unplannedAssignedTo.join(","));
      }

      const data = await apiRequest(`/sn-actions/unplanned?${queryParams.toString()}`);
      setUnplannedResults(data);
    } catch (err) {
      setError(err.message || "Failed to search unplanned work");
    } finally {
      setUnplannedLoading(false);
    }
  };

  const exportUnplannedWork = () => {
    if (!unplannedResults || !unplannedResults.tasks || unplannedResults.tasks.length === 0) {
      return;
    }

    // Convert data to CSV
    const headers = ["Number", "Type", "Short Description", "Description", "Assigned To", "State", "Closed Date"];
    const csvRows = [headers.join(",")];

    unplannedResults.tasks.forEach(task => {
      const row = [
        displayText(task.number),
        displayText(task.sys_class_name).replace('sn_hr_core_', '').replace('_', ' ').replace('sc task', 'Task'),
        `"${displayText(task.short_description).replace(/"/g, '""')}"`,
        `"${displayText(task.description, '').replace(/"/g, '""')}"`,
        displayText(task.assigned_to, "Unassigned"),
        displayText(task.state),
        task.closed_at && task.closed_at.value ? new Date(task.closed_at.value).toLocaleDateString() : ""
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").substring(0, 19);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `unplanned-work-${timestamp}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadTeamMetrics = async () => {
    setMetricsLoading(true);
    setError(null);
    
    try {
      const data = await apiRequest(`/sn-actions/team-metrics?period=${metricsPeriod}`);
      setMetricsData(data);
    } catch (err) {
      setError(err.message || "Failed to load team metrics");
    } finally {
      setMetricsLoading(false);
    }
  };

  // Render charts when metrics data changes
  useEffect(() => {
    if (!metricsData || activeTab !== "metrics") return;

    // Helper function to create chart data
    const createChartData = (members) => {
      const labels = members.map(m => m.name);
      
      return {
        labels,
        datasets: [
          {
            label: 'Incidents',
            data: members.map(m => m.incidents),
            backgroundColor: 'rgba(54, 162, 235, 0.8)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
          },
          {
            label: 'Tasks',
            data: members.map(m => m.tasks),
            backgroundColor: 'rgba(75, 192, 192, 0.8)',
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 1,
          },
        ],
      };
    };

    // Axis, legend and grid colors follow the production light/dark theme.
    const themeSource = document.getElementById('basisInfraChart') || document.getElementById('appSecurityChart') || document.body;
    const themeStyle = getComputedStyle(themeSource);
    const textColor = themeStyle.getPropertyValue('--text-secondary').trim() || '#526670';
    const gridColor = themeStyle.getPropertyValue('--border').trim() || 'rgba(127, 127, 127, 0.2)';

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: textColor,
          },
        },
        tooltip: {
          callbacks: {
            footer: (tooltipItems) => {
              const dataIndex = tooltipItems[0].dataIndex;
              const member = tooltipItems[0].chart.config._config.data.labels[dataIndex];
              const dataset = tooltipItems[0].chart.config._config.data.datasets;
              const total = dataset.reduce((sum, ds) => sum + ds.data[dataIndex], 0);
              return `Total: ${total}`;
            },
          },
        },
        // Custom plugin to display count in the middle of each bar
        datalabels: {
          anchor: 'center',
          align: 'center',
          color: '#ffffff',
          font: {
            weight: 'bold',
            size: 12,
          },
          formatter: (value) => {
            return value > 0 ? value : '';
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColor,
          },
          grid: {
            color: gridColor,
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: textColor,
            stepSize: 1,
          },
          grid: {
            color: gridColor,
          },
        },
      },
      // Make bars closer together
      barPercentage: 0.9,
      categoryPercentage: 0.8,
    };

    // Destroy existing charts if they exist
    const basisChart = ChartJS.getChart('basisInfraChart');
    if (basisChart) basisChart.destroy();
    const appChart = ChartJS.getChart('appSecurityChart');
    if (appChart) appChart.destroy();

    // Create Basis & Infrastructure Team chart
    if (metricsData.basis_infra_team.members.length > 0) {
      const basisCtx = document.getElementById('basisInfraChart');
      if (basisCtx) {
        new ChartJS(basisCtx, {
          type: 'bar',
          data: createChartData(metricsData.basis_infra_team.members),
          options: chartOptions,
        });
      }
    }

    // Create Application Security Team chart
    if (metricsData.app_security_team.members.length > 0) {
      const appCtx = document.getElementById('appSecurityChart');
      if (appCtx) {
        new ChartJS(appCtx, {
          type: 'bar',
          data: createChartData(metricsData.app_security_team.members),
          options: chartOptions,
        });
      }
    }
  }, [metricsData, activeTab, theme]);

  return (
    <div className="cug-root sn-root">
      <div className="cug-header">
        <div className="cug-header-top">
          <div className="logo-mark logo-mark-route"><ToolIcon toolId="servicenow-actions" size={19} /></div>
          <div>
            <div className="cug-title">ServiceNow Actions</div>
            <div className="cug-subtitle">Manage sprints and stories for your product and assignment group</div>
          </div>
        </div>
      </div>

      <div className="sn-toolbar-wrap">
        <div className="sn-toolbar-row">
          <div className="tab-bar" style={{ marginBottom: 0 }}>
            <button className={`tab-btn ${activeTab === "view" ? "active" : ""}`} onClick={() => setActiveTab("view")}>View Stories</button>
            <button className={`tab-btn ${activeTab === "create" ? "active" : ""}`} onClick={() => setActiveTab("create")}>Create A Story</button>
            <button className={`tab-btn ${activeTab === "multi" ? "active" : ""}`} onClick={() => setActiveTab("multi")}>Story for Multiple Sprints</button>
            <button className={`tab-btn ${activeTab === "delete" ? "active" : ""}`} onClick={() => setActiveTab("delete")}>Delete Stories</button>
            <button className={`tab-btn ${activeTab === "unplanned" ? "active" : ""}`} onClick={() => setActiveTab("unplanned")}>Unplanned Work</button>
            <button className={`tab-btn ${activeTab === "metrics" ? "active" : ""}`} onClick={() => setActiveTab("metrics")}>Team Metrics</button>
          </div>
          <button
            onClick={loadSprints}
            disabled={loading}
            title="Refresh stories and sprints"
            className="compare-btn"
          >
            {loading ? (
              <>
                <span className="spinner" />
                Refreshing…
              </>
            ) : (
              <>
                <svg
                  width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                >
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      <div className="cug-body sn-cug-body">

        {result && (
          <div className={`cug-result ${result.success ? "cug-result-success" : "cug-result-error"}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
              <div>
                <div className="cug-result-title">{result.success ? "Success" : "Error"}</div>
                <div>{result.message}</div>
              </div>
              <button
                onClick={() => setResult(null)}
                style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "16px", padding: "0 4px", opacity: 0.7 }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="error-banner" style={{ marginBottom: "0" }}>
            <strong>Error:</strong> {" "}{error}
            <button className="error-banner-close" onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {loading && !sprints ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading sprints…</p>
          </div>
        ) : sprints && sprints.length > 0 && activeTab === "view" ? (
          <div className="sn-sprints-list">
            <div className="sn-view-filter-bar">
              <label className="control-label" style={{ marginBottom: 0, whiteSpace: "nowrap" }}>Filter by Assignee</label>
              <select
                className="form-control sn-assignee-filter-select"
                value={viewAssigneeFilter}
                onChange={(e) => setViewAssigneeFilter(e.target.value)}
              >
                <option value="">— All Assignees —</option>
                {Array.from(
                  new Set(
                    Object.values(sprintStories)
                      .flat()
                      .map(s => displayText(s.assigned_to, ""))
                      .filter(Boolean)
                  )
                ).sort().map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              {viewAssigneeFilter && (
                <button
                  className="compare-btn"
                  onClick={() => setViewAssigneeFilter("")}
                  style={{ whiteSpace: "nowrap" }}
                >
                  Clear
                </button>
              )}
            </div>
            {sprints.map(sprint => (
              <SprintView 
                key={sprint.sys_id}
                sprint={sprint}
                stories={sprintStories[sprint.sys_id] || []}
                onCreateStory={(sprintId) => {
                  setSingleSprintId(sprintId);
                  setActiveTab("create");
                }}
                onCreateMultipleStories={() => {
                  setMultiSprintIds([]);
                  setActiveTab("multi");
                }}
                loading={loadingSprintId === sprint.sys_id || creatingStory}
                assigneeFilter={viewAssigneeFilter}
              />
            ))}
          </div>
        ) : sprints && sprints.length > 0 && activeTab === "create" ? (
          <div className="sn-panel-card">
            <h3>Create A Story</h3>
            <div style={{ display: "flex", gap: "16px" }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="control-label">Task Type *</label>
                <select className="form-control" value={singleTaskType} onChange={(e) => setSingleTaskType(e.target.value)} disabled={creatingStory}>
                  <option value="">— Select Task Type —</option>
                  {TASK_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="control-label">Application *</label>
                <select className="form-control" value={singleAppCode} onChange={(e) => setSingleAppCode(e.target.value)} disabled={creatingStory}>
                  <option value="">— Select Application —</option>
                  {APP_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="control-label">Epic</label>
              <select className="form-control" value={singleEpicId} onChange={(e) => setSingleEpicId(e.target.value)} disabled={creatingStory}>
                <option value="">— Select an Epic —</option>
                {(epics || []).map((ep) => (
                  <option key={ep.sys_id} value={ep.sys_id}>{ep.name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", gap: "16px" }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="control-label">Sprint</label>
                <select className="form-control" value={singleSprintId} onChange={(e) => setSingleSprintId(e.target.value)} disabled={creatingStory}>
                  <option value="">— Select a Sprint —</option>
                  {sprints.map((s) => (
                    <option key={s.sys_id} value={s.sys_id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ flex: "0 0 140px" }}>
                <label className="control-label">Story Points</label>
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g. 2"
                  value={singleStoryPoints}
                  onChange={(e) => setSingleStoryPoints(e.target.value)}
                  disabled={creatingStory}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label">Short Description *</label>
              <input className="form-control" value={singleSummary} onChange={(e) => setSingleSummary(e.target.value)} disabled={creatingStory} placeholder="Enter story description text after the prefix" />
              <div className="sn-short-description-options">
                <label className="sn-inline-checkbox">
                  <input type="checkbox" checked={singleIncludeSprint} onChange={(e) => setSingleIncludeSprint(e.target.checked)} disabled={creatingStory} />
                  <span>Include Sprint Number in Short Description</span>
                </label>
                <label className="sn-inline-checkbox">
                  <input type="checkbox" checked={singleIncludeMonth} onChange={(e) => setSingleIncludeMonth(e.target.checked)} disabled={creatingStory} />
                  <span>Include Month in Short Description</span>
                </label>
              </div>
              <div className="form-hint">Format preview: HRTSI-&lt;TaskType&gt;-&lt;Application&gt;{singleIncludeSprint ? "-Sprint###" : ""}{singleIncludeMonth ? "-&lt;MonYYYY&gt;" : ""}-&lt;YourText&gt;</div>
            </div>
            <div className="form-group">
              <label className="control-label">Description</label>
              <textarea className="form-control form-textarea" rows="6" value={singleDescription} onChange={(e) => setSingleDescription(e.target.value)} disabled={creatingStory} placeholder="Detailed description (optional)" />
            </div>
            <div className="sn-panel-actions">
              <button className="compare-btn sn-primary-btn" onClick={submitCreateSingle} disabled={creatingStory || !singleTaskType || !singleAppCode || !singleSummary.trim()}>
                {creatingStory ? "Creating…" : "Create Story"}
              </button>
            </div>
          </div>
        ) : sprints && sprints.length > 0 && activeTab === "multi" ? (
          <div className="sn-panel-card">
            <h3>Story for Multiple Sprints</h3>
            <div style={{ display: "flex", gap: "16px" }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="control-label">Task Type *</label>
                <select className="form-control" value={multiTaskType} onChange={(e) => setMultiTaskType(e.target.value)} disabled={creatingStory} required>
                  <option value="">— Select Task Type —</option>
                  {TASK_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="control-label">Application *</label>
                <select className="form-control" value={multiAppCode} onChange={(e) => setMultiAppCode(e.target.value)} disabled={creatingStory} required>
                  <option value="">— Select Application —</option>
                  {APP_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="control-label">Epic *</label>
              <select className="form-control" value={multiEpicId} onChange={(e) => setMultiEpicId(e.target.value)} disabled={creatingStory} required>
                <option value="">— Select an Epic —</option>
                {(epics || []).map((ep) => (
                  <option key={ep.sys_id} value={ep.sys_id}>{ep.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="control-label">Target Sprints *</label>
              <div className="sn-sprint-targets">
                {sprints.map((s) => (
                  <label key={s.sys_id} className="sn-sprint-target-row">
                    <input
                      type="checkbox"
                      checked={multiSprintIds.includes(s.sys_id)}
                      onChange={() => toggleMultiSprint(s.sys_id)}
                      disabled={creatingStory}
                    />
                    <span>{s.name}</span>
                  </label>
                ))}
              </div>
              <div className="form-hint">{multiSprintIds.length} sprint(s) selected</div>
            </div>
            <div className="form-group" style={{ maxWidth: "220px" }}>
              <label className="control-label">Story Points *</label>
              <input
                className="form-control"
                type="number"
                min="0"
                step="1"
                placeholder="e.g. 2"
                value={multiStoryPoints}
                onChange={(e) => setMultiStoryPoints(e.target.value)}
                disabled={creatingStory}
                required
              />
            </div>
            <div className="form-group">
              <label className="control-label">Short Description *</label>
              <textarea
                className="form-control"
                rows="1"
                value={multiStoriesText}
                onChange={(e) => setMultiStoriesText(e.target.value)}
                disabled={creatingStory}
                placeholder="Enter short description…"
                style={{ minHeight: "40px", height: "40px", resize: "none", overflowY: "hidden" }}
              />
              <div className="sn-short-description-options">
                <label className="sn-inline-checkbox">
                  <input type="checkbox" checked={multiIncludeSprint} onChange={(e) => setMultiIncludeSprint(e.target.checked)} disabled={creatingStory} />
                  <span>Include Sprint Number in Short Description</span>
                </label>
                <label className="sn-inline-checkbox">
                  <input type="checkbox" checked={multiIncludeMonth} onChange={(e) => setMultiIncludeMonth(e.target.checked)} disabled={creatingStory} />
                  <span>Include Month in Short Description</span>
                </label>
              </div>
              <div className="form-hint">Format preview: HRTSI-&lt;TaskType&gt;-&lt;Application&gt;{multiIncludeSprint ? "-Sprint###" : ""}{multiIncludeMonth ? "-&lt;MonYYYY&gt;" : ""}-&lt;YourText&gt;</div>
              <div className="form-hint">{multiStoriesText.trim() ? "1 story will be created in each selected sprint" : "0 stories will be created in each selected sprint"}</div>
            </div>
            <div className="form-group">
              <label className="control-label">Description</label>
              <textarea
                className="form-control form-textarea"
                rows="6"
                value={multiDescription}
                onChange={(e) => setMultiDescription(e.target.value)}
                disabled={creatingStory}
                placeholder="Detailed description. Applied to each story."
              />
            </div>
            <div className="sn-panel-actions">
              <button className="compare-btn sn-primary-btn" onClick={submitCreateMultiple} disabled={creatingStory || !multiTaskType || !multiAppCode || !multiEpicId || multiSprintIds.length === 0 || String(multiStoryPoints || "").trim() === "" || !multiStoriesText.trim()}>
                {creatingStory ? "Creating…" : "Create Stories"}
              </button>
            </div>
          </div>
        ) : sprints && sprints.length > 0 && activeTab === "delete" ? (
          <div className="sn-panel-card">
            <div className="sn-delete-panel-header">
              <h3 style={{ margin: 0 }}>Delete Stories</h3>
              {selectedStoriesForDelete.length > 0 && !confirmDeleteSelected && (
                <button
                  className="compare-btn sn-primary-btn"
                  onClick={() => { setConfirmDeleteSelected(true); setConfirmDeleteCheckbox(false); }}
                  disabled={creatingStory || !!deletingStoryId}
                >
                  Review {selectedStoriesForDelete.length} Story(ies)
                </button>
              )}
            </div>
            <div className="form-group">
              <label className="control-label">Choose Sprint</label>
              <select className="form-control" value={deleteSprintId} onChange={(e) => {
                setDeleteSprintId(e.target.value);
                setSelectedStoriesForDelete([]);
                setConfirmDeleteSelected(false);
                setConfirmDeleteCheckbox(false);
              }} disabled={creatingStory || !!deletingStoryId || confirmDeleteSelected}>
                <option value="">— Select a Sprint —</option>
                {sprints.map((s) => (
                  <option key={s.sys_id} value={s.sys_id}>{s.name}</option>
                ))}
              </select>
            </div>

            {deleteSprintId && !confirmDeleteSelected && (
              <div className="sn-delete-list">
                {(sprintStories[deleteSprintId] || []).length === 0 ? (
                  <div className="sn-empty-state">No stories found in selected sprint</div>
                ) : (
                  <>
                    <div className="sn-stories-list" style={{ marginTop: 0 }}>
                      <div className="sn-stories-header sn-delete-stories-header">
                        <div></div>
                        <div className="sn-story-col sn-story-col-key">Story #</div>
                        <div className="sn-story-col sn-story-col-summary">Summary</div>
                        <div className="sn-story-col sn-story-col-points">Points</div>
                        <div className="sn-story-col sn-story-col-status">Status</div>
                        <div className="sn-story-col sn-story-col-assignee">Assignee</div>
                      </div>
                      <div className="sn-story-row sn-delete-select-all-row">
                        <label className="sn-delete-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedStoriesForDelete.length === (sprintStories[deleteSprintId] || []).length && selectedStoriesForDelete.length > 0}
                            onChange={() => {
                              if (selectedStoriesForDelete.length === (sprintStories[deleteSprintId] || []).length) {
                                setSelectedStoriesForDelete([]);
                              } else {
                                setSelectedStoriesForDelete((sprintStories[deleteSprintId] || []).map(s => s.sys_id));
                              }
                            }}
                            disabled={creatingStory || !!deletingStoryId}
                          />
                        </label>
                        <div className="sn-story-col" style={{ gridColumn: "2 / -1", color: "var(--text-muted)", fontSize: 12 }}>
                          {selectedStoriesForDelete.length > 0 ? `${selectedStoriesForDelete.length} of ${(sprintStories[deleteSprintId] || []).length} selected` : "Select all"}
                        </div>
                      </div>
                      {(sprintStories[deleteSprintId] || []).map((story) => (
                        <div
                          key={story.sys_id}
                          className={`sn-story-row sn-delete-story-row${selectedStoriesForDelete.includes(story.sys_id) ? " sn-delete-row-selected" : ""}`}
                          onClick={() => toggleStorySelection(story.sys_id)}
                          style={{ cursor: "pointer" }}
                        >
                          <label className="sn-delete-checkbox" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedStoriesForDelete.includes(story.sys_id)}
                              onChange={() => toggleStorySelection(story.sys_id)}
                              disabled={creatingStory || !!deletingStoryId}
                            />
                          </label>
                          <div className="sn-story-col sn-story-col-key">{displayText(story.number)}</div>
                          <div className="sn-story-col sn-story-col-summary" title={displayText(story.title)}>{displayText(story.title)}</div>
                          <div className="sn-story-col sn-story-col-points">{displayText(story.story_points, "—")}</div>
                          <div className="sn-story-col sn-story-col-status">{displayText(story.state)}</div>
                          <div className="sn-story-col sn-story-col-assignee">{displayText(story.assigned_to, "Unassigned")}</div>
                        </div>
                      ))}
                    </div>
                    {selectedStoriesForDelete.length > 0 && (
                      <div style={{ height: 0 }} />
                    )}
                  </>
                )}
              </div>
            )}

            {confirmDeleteSelected && selectedStoriesForDelete.length > 0 && (
              <div className="sn-delete-confirmation">
                <div className="sn-confirmation-header">
                  <h4>Review Stories to Delete</h4>
                </div>

                <div className="sn-confirmation-summary">
                  <div className="sn-summary-label">Selected Stories ({selectedStoriesForDelete.length}):</div>
                  <div className="sn-stories-list" style={{ marginTop: 0 }}>
                    <div className="sn-stories-header sn-delete-stories-header">
                      <div></div>
                      <div className="sn-story-col sn-story-col-key">Story #</div>
                      <div className="sn-story-col sn-story-col-summary">Summary</div>
                      <div className="sn-story-col sn-story-col-points">Points</div>
                      <div className="sn-story-col sn-story-col-status">Status</div>
                      <div className="sn-story-col sn-story-col-assignee">Assignee</div>
                    </div>
                    {(sprintStories[deleteSprintId] || [])
                      .filter(story => selectedStoriesForDelete.includes(story.sys_id))
                      .map(story => (
                        <div key={story.sys_id} className="sn-story-row sn-delete-stories-header">
                          <div></div>
                          <div className="sn-story-col sn-story-col-key">{displayText(story.number)}</div>
                          <div className="sn-story-col sn-story-col-summary" title={displayText(story.title)}>{displayText(story.title)}</div>
                          <div className="sn-story-col sn-story-col-points">{displayText(story.story_points, "—")}</div>
                          <div className="sn-story-col sn-story-col-status">{displayText(story.state)}</div>
                          <div className="sn-story-col sn-story-col-assignee">{displayText(story.assigned_to, "Unassigned")}</div>
                        </div>
                      ))
                    }
                  </div>
                </div>

                <div className="sn-caution-message">
                  <div className="sn-caution-icon">⚠️</div>
                  <div className="sn-caution-text">
                    <strong>Caution:</strong> Deleting these stories is permanent and cannot be undone. This action will remove all associated data.
                  </div>
                </div>

                <div className="sn-confirmation-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={confirmDeleteCheckbox}
                      onChange={(e) => setConfirmDeleteCheckbox(e.target.checked)}
                      disabled={!!deletingStoryId}
                    />
                    <span>I understand I will delete {selectedStoriesForDelete.length} story(ies) and this action cannot be undone</span>
                  </label>
                </div>

                <div className="sn-confirmation-actions">
                  <button
                    className="compare-btn"
                    onClick={() => {
                      setConfirmDeleteSelected(false);
                      setConfirmDeleteCheckbox(false);
                    }}
                    disabled={!!deletingStoryId}
                  >
                    Cancel
                  </button>
                  <button
                    className="compare-btn sn-delete-confirm-btn"
                    onClick={handleDeleteBatch}
                    disabled={!confirmDeleteCheckbox || !!deletingStoryId}
                  >
                    {deletingStoryId === "batch" ? "Deleting…" : "Delete Stories"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === "unplanned" ? (
          <div className="sn-panel-card">
            <h3 style={{ margin: "0 0 20px 0" }}>Unplanned Work</h3>
            
            {/* Filter Panel */}
            <div className="sn-unplanned-filters" style={{ marginBottom: "24px", padding: "20px", background: "var(--card-bg)", borderRadius: "8px", border: "1px solid var(--border-color)" }}>
              
              {/* Row 1: Assignment Group, Assigned To, Task Type */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="control-label">Assignment Group</label>
                  <select 
                    className="form-control" 
                    value={unplannedAssignmentGroup}
                    onChange={(e) => {
                      setUnplannedAssignmentGroup(e.target.value);
                      setUnplannedAssignedTo(""); // Clear assigned to when group changes
                    }}
                  >
                    <option value="">— Select Group —</option>
                    <option value="347921630fd68a006ee822d8b1050ebc">KGS HRT Application Security</option>
                    <option value="b1b9e1630fd68a006ee822d8b1050e7e">KBS HR SAP Basis System Admin</option>
                    <option value="b9b9e1630fd68a006ee822d8b1050e7b">KGS HRT Cloud Infrastructure</option>
                    <option value="4eb9e1630fd68a006ee822d8b1050e8b">KBS HR SAP Transports - Basis Team</option>
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: 0, position: "relative" }} ref={assigneeDropdownRef}>
                  <label className="control-label">Assigned To (Multi-select)</label>
                  <div 
                    className="form-control" 
                    onClick={() => setUnplannedAssigneeDropdownOpen(!unplannedAssigneeDropdownOpen)}
                    style={{ 
                      cursor: "pointer", 
                      minHeight: "34px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}
                  >
                    <span style={{ color: unplannedAssignedTo.length === 0 ? "var(--text-muted)" : "inherit" }}>
                      {unplannedAssignedTo.length === 0 
                        ? "Select people..." 
                        : `${unplannedAssignedTo.length} selected`}
                    </span>
                    <span style={{ marginLeft: "8px" }}>▼</span>
                  </div>
                  {unplannedAssigneeDropdownOpen && (
                    <div style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "var(--bg-surface)",
                      border: "1px solid var(--border)",
                      borderRadius: "4px",
                      maxHeight: "250px",
                      overflowY: "auto",
                      zIndex: 1000,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                      marginTop: "4px"
                    }}>
                      {filteredAssignees.map((assignee) => (
                        <label 
                          key={assignee.value} 
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 12px",
                            cursor: "pointer",
                            borderBottom: "1px solid var(--border)",
                            backgroundColor: unplannedAssignedTo.includes(assignee.value) ? "var(--bg-raised)" : "var(--bg-surface)"
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--bg-hover)"}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = unplannedAssignedTo.includes(assignee.value) ? "var(--bg-raised)" : "var(--bg-surface)"}
                        >
                          <input
                            type="checkbox"
                            checked={unplannedAssignedTo.includes(assignee.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setUnplannedAssignedTo([...unplannedAssignedTo, assignee.value]);
                              } else {
                                setUnplannedAssignedTo(unplannedAssignedTo.filter(id => id !== assignee.value));
                              }
                            }}
                            style={{ marginRight: "8px", cursor: "pointer" }}
                          />
                          <span>{assignee.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group" style={{ marginBottom: 0, position: "relative" }} ref={taskTypeDropdownRef}>
                  <label className="control-label">Task Type (Multi-select)</label>
                  <div 
                    className="form-control" 
                    onClick={() => setUnplannedTaskTypeDropdownOpen(!unplannedTaskTypeDropdownOpen)}
                    style={{ 
                      cursor: "pointer", 
                      minHeight: "34px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}
                  >
                    <span style={{ color: unplannedTaskTypes.length === 0 ? "var(--text-muted)" : "inherit" }}>
                      {unplannedTaskTypes.length === 0 
                        ? "Select task types..." 
                        : `${unplannedTaskTypes.length} selected`}
                    </span>
                    <span style={{ marginLeft: "8px" }}>▼</span>
                  </div>
                  {unplannedTaskTypeDropdownOpen && (
                    <div style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "var(--bg-surface)",
                      border: "1px solid var(--border)",
                      borderRadius: "4px",
                      maxHeight: "250px",
                      overflowY: "auto",
                      zIndex: 1000,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                      marginTop: "4px"
                    }}>
                      {[
                        { value: "incident", label: "Incident" },
                        { value: "sc_task", label: "Task" },
                        { value: "sn_hr_core_case", label: "HR Case" },
                        { value: "change_request", label: "Change Request" }
                      ].map((taskType) => (
                        <label 
                          key={taskType.value} 
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 12px",
                            cursor: "pointer",
                            borderBottom: "1px solid var(--border)",
                            backgroundColor: unplannedTaskTypes.includes(taskType.value) ? "var(--bg-raised)" : "var(--bg-surface)"
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--bg-hover)"}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = unplannedTaskTypes.includes(taskType.value) ? "var(--bg-raised)" : "var(--bg-surface)"}
                        >
                          <input
                            type="checkbox"
                            checked={unplannedTaskTypes.includes(taskType.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setUnplannedTaskTypes([...unplannedTaskTypes, taskType.value]);
                              } else {
                                setUnplannedTaskTypes(unplannedTaskTypes.filter(t => t !== taskType.value));
                              }
                            }}
                            style={{ marginRight: "8px", cursor: "pointer" }}
                          />
                          <span>{taskType.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

              </div>

              {/* Row 2: Opened After, Closed After, State */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="control-label">Opened After</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={unplannedOpenedAfter}
                    onChange={(e) => setUnplannedOpenedAfter(e.target.value)}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="control-label">Closed After</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={unplannedClosedAfter}
                    onChange={(e) => setUnplannedClosedAfter(e.target.value)}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0, position: "relative" }} ref={stateDropdownRef}>
                  <label className="control-label">State (Multi-select)</label>
                  <div 
                    className="form-control" 
                    onClick={() => setUnplannedStateDropdownOpen(!unplannedStateDropdownOpen)}
                    style={{ 
                      cursor: "pointer", 
                      minHeight: "34px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}
                  >
                    <span style={{ color: unplannedStates.length === 0 ? "var(--text-muted)" : "inherit" }}>
                      {unplannedStates.length === 0 
                        ? "Select states..." 
                        : `${unplannedStates.length} selected`}
                    </span>
                    <span style={{ marginLeft: "8px" }}>▼</span>
                  </div>
                  {unplannedStateDropdownOpen && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "var(--bg-surface)",
                    border: "1px solid var(--border)",
                    borderRadius: "4px",
                    maxHeight: "250px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                    marginTop: "4px"
                  }}>
                    {[
                      { value: "1", label: "New" },
                      { value: "2", label: "In Progress" },
                      { value: "3", label: "On Hold" },
                      { value: "7", label: "Closed" },
                      { value: "12", label: "Cancelled" }
                    ].map((state) => (
                      <label 
                        key={state.value} 
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid var(--border)",
                          backgroundColor: unplannedStates.includes(state.value) ? "var(--bg-raised)" : "var(--bg-surface)"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--bg-hover)"}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = unplannedStates.includes(state.value) ? "var(--bg-raised)" : "var(--bg-surface)"}
                      >
                        <input
                          type="checkbox"
                          checked={unplannedStates.includes(state.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setUnplannedStates([...unplannedStates, state.value]);
                            } else {
                              setUnplannedStates(unplannedStates.filter(s => s !== state.value));
                            }
                          }}
                          style={{ marginRight: "8px", cursor: "pointer" }}
                        />
                        <span>{state.label}</span>
                      </label>
                    ))}
                  </div>
                  )}
                </div>

              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                <button 
                  className="compare-btn sn-primary-btn"
                  onClick={searchUnplannedWork}
                  disabled={unplannedLoading || !unplannedAssignmentGroup}
                >
                  {unplannedLoading ? "Searching..." : "Search"}
                </button>
                <button 
                  className="compare-btn"
                  onClick={() => {
                    setUnplannedAssignmentGroup("");
                    setUnplannedTaskTypes(["incident", "sc_task", "sn_hr_core_case", "change_request"]);
                    setUnplannedStates([]);
                    setUnplannedOpenedAfter("");
                    setUnplannedClosedAfter("");
                    setUnplannedAssignedTo([]);
                    setUnplannedAssigneeDropdownOpen(false);
                    setUnplannedTaskTypeDropdownOpen(false);
                    setUnplannedStateDropdownOpen(false);
                    setUnplannedResults(null);
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="sn-unplanned-results">
              {unplannedLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner" />
                  <p>Searching for unplanned work…</p>
                </div>
              ) : unplannedResults && unplannedResults.tasks ? (
                <div>
                  <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ color: "var(--text-muted)", fontSize: "14px" }}>
                      Found {unplannedResults.count} task{unplannedResults.count !== 1 ? 's' : ''}
                    </div>
                    {unplannedResults.count > 0 && (
                      <button 
                        className="compare-btn" 
                        onClick={exportUnplannedWork}
                        style={{ display: "flex", alignItems: "center", gap: "6px" }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Export CSV
                      </button>
                    )}
                  </div>
                  {unplannedResults.count === 0 ? (
                    <div className="sn-empty-state">No unplanned work found matching your criteria</div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                        <thead>
                          <tr style={{ borderBottom: "2px solid var(--border-color)", background: "var(--card-bg)" }}>
                            <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, minWidth: "120px" }}>Number</th>
                            <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, minWidth: "100px" }}>Type</th>
                            <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, minWidth: "300px" }}>Short Description</th>
                            <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, minWidth: "150px" }}>Assigned To</th>
                            <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, minWidth: "100px" }}>State</th>
                            <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, minWidth: "110px" }}>Closed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unplannedResults.tasks.map((task) => (
                            <tr key={task.sys_id.value} style={{ borderBottom: "1px solid var(--border-color)" }}>
                              <td style={{ padding: "12px 16px", fontWeight: 500 }}>
                                {displayText(task.number)}
                              </td>
                              <td style={{ padding: "12px 16px", fontSize: "12px" }}>
                                {displayText(task.sys_class_name).replace('sn_hr_core_', '').replace('_', ' ').replace('sc task', 'Task')}
                              </td>
                              <td style={{ padding: "12px 16px" }}>
                                <div>{displayText(task.short_description)}</div>
                                {task.description && task.description.value && (
                                  <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" }}>
                                    {displayText(task.description).substring(0, 150)}
                                    {displayText(task.description).length > 150 ? "..." : ""}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: "12px 16px", fontSize: "13px" }}>
                                {displayText(task.assigned_to, "Unassigned")}
                              </td>
                              <td style={{ padding: "12px 16px" }}>
                                {displayText(task.state)}
                              </td>
                              <td style={{ padding: "12px 16px", fontSize: "12px" }}>
                                {task.closed_at && task.closed_at.value 
                                  ? new Date(task.closed_at.value).toLocaleDateString()
                                  : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="sn-empty-state">Select an assignment group and click Search to view unplanned work</div>
              )}
            </div>
          </div>
        ) : activeTab === "metrics" ? (
          <div className="sn-panel-card">
            <h3 style={{ margin: "0 0 20px 0" }}>Team Metrics</h3>
            
            {/* Time Period Selector */}
            <div style={{ marginBottom: "24px", display: "flex", gap: "8px", alignItems: "center" }}>
              <label style={{ fontWeight: 500, marginRight: "8px" }}>Time Period:</label>
              <select 
                className="form-control" 
                style={{ maxWidth: "200px" }}
                value={metricsPeriod}
                onChange={(e) => setMetricsPeriod(e.target.value)}
              >
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="quarter">Quarter</option>
                <option value="ytd">YTD</option>
                <option value="one_year">1 Year</option>
              </select>
              <button 
                className="compare-btn sn-primary-btn"
                onClick={() => loadTeamMetrics()}
                disabled={metricsLoading}
              >
                {metricsLoading ? "Loading..." : "Load Metrics"}
              </button>
            </div>

            {/* Charts */}
            {metricsLoading ? (
              <div className="loading-state">
                <div className="loading-spinner" />
                <p>Loading team metrics…</p>
              </div>
            ) : metricsData ? (
              <div>
                {/* Basis & Infrastructure Team Chart */}
                <div style={{ marginBottom: "40px" }}>
                  <h4 style={{ margin: "0 0 16px 0", fontSize: "18px" }}>Basis & Infrastructure Team</h4>
                  <div style={{ marginBottom: "12px", padding: "12px", background: "var(--bg-raised)", borderRadius: "8px", fontSize: "14px" }}>
                    <strong>Total:</strong> {metricsData.basis_infra_team.grand_total} items 
                    ({metricsData.basis_infra_team.total_incidents} incidents, {metricsData.basis_infra_team.total_tasks} tasks)
                  </div>
                  {metricsData.basis_infra_team.members.length === 0 ? (
                    <div className="sn-empty-state">No completed work found for this team in the selected period</div>
                  ) : (
                    <canvas id="basisInfraChart" style={{ maxHeight: "400px" }}></canvas>
                  )}
                </div>

                {/* Application Security Team Chart */}
                <div>
                  <h4 style={{ margin: "0 0 16px 0", fontSize: "18px" }}>Application Security Team</h4>
                  <div style={{ marginBottom: "12px", padding: "12px", background: "var(--bg-raised)", borderRadius: "8px", fontSize: "14px" }}>
                    <strong>Total:</strong> {metricsData.app_security_team.grand_total} items 
                    ({metricsData.app_security_team.total_incidents} incidents, {metricsData.app_security_team.total_tasks} tasks)
                  </div>
                  {metricsData.app_security_team.members.length === 0 ? (
                    <div className="sn-empty-state">No completed work found for this team in the selected period</div>
                  ) : (
                    <canvas id="appSecurityChart" style={{ maxHeight: "400px" }}></canvas>
                  )}
                </div>
              </div>
            ) : (
              <div className="sn-empty-state">Select a time period and click "Load Metrics" to view team productivity data</div>
            )}
          </div>
        ) : (
          <div className="sn-empty-state-large">
            <div className="empty-icon">📋</div>
            <h2>No sprints found</h2>
            <p>No sprints are available for your product and assignment group.</p>
          </div>
        )}
      </div>
    </div>
  );
}
